import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginManager, registerManager, getManagerMe } from '../../api/leagues';
import '../../styles/global.css';

const ManagerLogin: React.FC = () => {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if already logged in as manager
    const checkAuth = async () => {
      const token = localStorage.getItem('manager_token');
      if (token) {
        try {
          // Temporarily set the token for the API call
          localStorage.setItem('token', token);
          await getManagerMe();
          navigate('/manager/dashboard');
        } catch {
          localStorage.removeItem('manager_token');
        } finally {
          localStorage.removeItem('token');
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, [navigate]);

  if (checkingAuth) {
    return <div className="loading">Loading...</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isRegistering && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isRegistering && !name.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsSubmitting(true);
    try {
      let data;
      if (isRegistering) {
        data = await registerManager(email, password, name);
      } else {
        data = await loginManager(email, password);
      }
      localStorage.setItem('manager_token', data.access_token);
      navigate(isRegistering ? '/manager/help' : '/manager/dashboard', isRegistering ? { state: { firstVisit: true } } : undefined);
    } catch (err: any) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="container">
      <div className="card">
        <h1 className="title">League Manager</h1>
        <h2 className="subtitle">{isRegistering ? 'Create Account' : 'Login'}</h2>
        <p className="description">
          Manage leagues and send invites to players
        </p>

        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              disabled={isSubmitting}
              required
            />
          </div>

          {isRegistering && (
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                disabled={isSubmitting}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={isSubmitting}
              required
            />
          </div>

          {isRegistering && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                disabled={isSubmitting}
                required
              />
            </div>
          )}

          {error && <div className="error">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Please wait...' : (isRegistering ? 'Create Account' : 'Login')}
          </button>
        </form>

        <div className="toggle-link">
          <button type="button" onClick={toggleMode} className="link-btn">
            {isRegistering ? 'Already have an account? Login' : 'Create Account'}
          </button>
        </div>

        <div className="toggle-link" style={{ marginTop: '20px' }}>
          <button type="button" onClick={() => navigate('/')} className="link-btn">
            Back to Player Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagerLogin;
